import {
  ImageScaleView,
  ImageScaleViewOpts,
  View,
} from '@play-co/timestep-core/ui';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { Match2RewardItem } from './Match2RewardItem';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { animatePuzzleRewards } from 'src/game/animation/rewardAnimation';

type Match2RewardsPanelOpts = Omit<ImageScaleViewOpts, 'image'> & {
  theme?: 'light' | 'dark';
  itemSize?: number;
  itemPadding?: number;
  itemOffsetX?: number;
  itemOffsetY?: number;
  itemLabelOffsetX?: number;
  itemLabelOffsetY?: number;
  itemPrefix?: string;
  itemLabelSize?: number;
  noBg?: boolean;
  centerOnOrigin?: boolean;
};

export class Match2RewardsPanel extends ImageScaleView {
  private rewardsContainer: FlexBoxView;
  private items: Match2RewardItem[] = [];
  private itemSize?: number;
  private itemPadding?: number;
  private itemOffsetX: number;
  private itemOffsetY: number;
  private itemLabelOffsetX: number;
  private itemLabelOffsetY: number;
  private itemPrefix?: string;
  private itemLabelSize?: number;
  private rewards?: Match2Reward[];

  constructor(opts: Match2RewardsPanelOpts = {}) {
    const width = opts.width ?? 494;
    const height = opts.height ?? 188;
    const theme = opts.theme ?? 'light';
    const slices = theme === 'light' ? 44 : 50;
    super({
      image: `assets/ui/match2/common/panel_sub_indent${
        theme === 'dark' ? '_alt' : ''
      }.png`,
      width,
      height,
      centerOnOrigin: opts.centerOnOrigin ?? true,
      centerAnchor: true,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: slices, right: slices },
        vertical: { top: slices, bottom: slices },
      },
      ...opts,
    });

    if (opts.noBg) {
      this.setImage(undefined);
    }

    const containerWidth = width - 15;
    const containerHeight = height - 20;

    this.itemSize = opts.itemSize;
    this.itemPadding = opts.itemPadding;
    this.itemOffsetX = opts.itemOffsetX ?? 0;
    this.itemOffsetY = opts.itemOffsetY ?? 0;
    this.itemLabelOffsetX = opts.itemLabelOffsetX ?? 0;
    this.itemLabelOffsetY = opts.itemLabelOffsetY ?? 0;
    this.itemPrefix = opts.itemPrefix;
    this.itemLabelSize = opts.itemLabelSize ?? 35;

    this.rewardsContainer = new FlexBoxView({
      superview: this,
      width: containerWidth,
      height: containerHeight,
      flex: {
        flexDirection: 'row',
        alignContent: 'center',
        justifyContent: 'space-evenly',
        flexWrap: 'wrap',
      },
      x: containerWidth * 0.5 + 7 + this.itemOffsetX,
      y: containerHeight * 0.5 + this.itemOffsetY,
      canHandleEvents: false,
      centerOnOrigin: true,
      centerAnchor: true,
    });
  }

  public setRewards(rewards: Match2Reward[]) {
    this.rewards = rewards;
    this.rewardsContainer.removeAllSubviews();
    rewards.forEach((reward) => {
      let item = this.items.find((item) => item.getParents().length === 0);

      if (!item) {
        item = new Match2RewardItem({
          size: this.itemSize ?? this.style.height * 0.65,
          itemPadding: this.itemPadding,
          labelOffsetX: this.itemLabelOffsetX,
          labelOffsetY: this.itemLabelOffsetY,
          prefix: this.itemPrefix,
          labelSize: this.itemLabelSize,
        });
        this.items.push(item);
      }

      item.setReward(reward);
      this.rewardsContainer.addSubview(item);
    });
  }

  public async grantRewards(root: View, stars?: number) {
    if (this.rewards) {
      await animatePuzzleRewards({ stars, rewards: this.rewards });
    }
  }
}
